import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import { StepType } from '@de/types/enums';
import type { Option, Question, Step } from '@de/types/types';
import { HOST_ONLY_CONTEXT_KEYS } from '@de/utils/decisions/embeddedDecisions';
import { FORBIDDEN_KEYS } from '@de/utils/interpolation/forbiddenKeys';
import { readStepFlowType } from './readStepFlowType';
import { readStepTrafficSource } from './readStepTrafficSource';

type FlowStateEntry = NonNullable<FlowState[string]>;

/**
 * Optional knobs for {@link buildFlowContext}.
 *
 * Callers on hot paths (e.g. ConfigurableStep) must resolve
 * `subjectLabelFallback` once from the entry step's `entryPoint`
 * config (via {@link getEntryPointConfigFromSteps}) and pass it here —
 * this helper stays pure and must not walk/sanitize DSL config itself
 * (that path can `console.warn` on malformed blocks).
 */
export type BuildFlowContextOptions = {
  /**
   * Author-owned fallback copy for {@link SUBJECT_LABEL_WITH_FALLBACK_KEY} when
   * `context.subject` is missing or the platform general sentinel
   * (`general`). Never hardcode domain-specific copy (e.g. "Higher
   * Education") into the runtime — authors set this per experience via
   * `entryPoint.subjectLabelFallback`.
   */
  subjectLabelFallback?: string;
};

/** Offerings / DCS general-default sentinel slug. */
const DCS_GENERAL_SUBJECT = 'general';

/**
 * Adaptive subject label token for DSL templates
 * (`{{context.subject_label_with_fallback}}`). Distinct from honest
 * `subject_label` (taxonomy display text stays e.g. "Undecided" for
 * `general`). Suitable for headings and other copy surfaces.
 */
export const SUBJECT_LABEL_WITH_FALLBACK_KEY = 'subject_label_with_fallback';

function createFlowContext(): Record<string, unknown> {
  return Object.create(null) as Record<string, unknown>;
}

/**
 * Whether an authored handle may name this context key. Host-only keys, such
 * as `decisions`, are filled by the page host after this context is built.
 */
function isSafeContextKey(key: string): boolean {
  if (FORBIDDEN_KEYS.has(key) || HOST_ONLY_CONTEXT_KEYS.has(key)) return false;
  if (key.endsWith('_label')) {
    const baseKey = key.slice(0, -'_label'.length);
    if (FORBIDDEN_KEYS.has(baseKey)) return false;
  }
  return true;
}

function setContextKey(
  context: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  if (!isSafeContextKey(key)) return;
  context[key] = value;
}

function hasContextKey(context: Record<string, unknown>, key: string): boolean {
  if (!isSafeContextKey(key)) return false;
  return Object.prototype.hasOwnProperty.call(context, key);
}

/**
 * Flatten a DE `flowState` snapshot into the `Record<string, unknown>`
 * shape DSL templates reference as `{{context.*}}` (per child SAD ADR-15
 * and ADR-12 §5.3).
 *
 * For each step that has selections, keys are emitted as follows:
 *   - Always: `<stepId>` — slug (or slug array for multi-select)
 *   - Always: `<stepId>_label` — display text; multi-select is a single
 *     human-readable string joined with `", "` (not an array), so mixed
 *     templates like `Hello {{context.skills_label}}!` stay readable
 *     without changing interpolator query/body joining
 *   - When recorded: `<leadFieldAssociated>` (+ `_label`) from the step or
 *     in-flight step data
 *   - When configured: `<customField>` (+ `_label`); omitted when the same
 *     step's `leadFieldAssociated` already owns the key
 *   - Per-option `leadFieldAssociated` / `customField` on `Option[]`
 *     payloads (multi-`formKey` / DCS triad): each option emits its own
 *     slug + `_label` companion, without overwriting step-level keys
 *
 * Adaptive subject label with authored fallback (Option B — site-wide
 * shared entry experience):
 *   - `subject_label_with_fallback` — human subject label when
 *     `context.subject` is a specific (non-`general`) slug; otherwise
 *     `options.subjectLabelFallback` (trimmed). Omitted when neither
 *     applies. Does **not** rewrite honest `subject_label` for `general`.
 *   - Callers resolve the fallback once from
 *     `entryPoint.subjectLabelFallback` and pass it via `options` so
 *     this helper stays free of DSL-config walks / warn side effects.
 *   - Author template example (headings or other copy):
 *     `Change Lives with a {{context.subject_label_with_fallback}} Degree`
 *     with `entryPoint.subjectLabelFallback` set per domain (e.g.
 *     Psychology → `"Higher Education"`).
 *
 * Collision rules for author-defined handles:
 *   - Within a step, `leadFieldAssociated` wins over `customField`.
 *   - Across steps, the same handle is last-write-wins by step iteration
 *     order (authors should keep `customField` / `leadFieldAssociated`
 *     values unique per flow).
 *
 * Author-defined handles (`customField` / `leadFieldAssociated`) are the
 * supported way to reference prior answers in DSL templates and data sources.
 * `<stepId>` keys are emitted for runtime/debug use but are not an
 * author-supported contract — ids are clone-unsafe and hyphenated segments
 * cannot be parsed by the interpolation engine.
 *
 * `MULTI_QUESTION` / `DCS` child questions are included via each parent's
 * `stepQuestions` array. `processFlowData` omits those child ids from the
 * top-level `steps` list, but `flowState` is still keyed by the child id
 * (TextQuestion writes under `question.id`).
 *
 * Steps without recorded selections are skipped. The result is a plain
 * object suitable for direct hand-off to `<DataSourceManagerProvider>`.
 *
 * The function is pure and `O(numSteps)`; callers should memoize on
 * `[flowState, steps, activeStep]`.
 *
 * When `activeStep` carries a step-level `flowType` (Result/Component
 * items per SAD v4 Decision 2), it is emitted as `context.flowType` for
 * downstream Offerings budget filtering.
 *
 * When `activeStep` carries a step-level `trafficSource`, it is emitted as
 * `context.trafficSource` and wins over URL-derived values downstream.
 */
export function buildFlowContext(
  flowState: FlowState | undefined,
  steps: readonly Step[] | undefined,
  activeStep?: Step,
  options?: BuildFlowContextOptions,
): Record<string, unknown> {
  const context = createFlowContext();
  if (!flowState || !steps) return context;

  for (const step of steps) {
    const stepId = step?.id;
    if (stepId) {
      const data = flowState[stepId];
      if (data) {
        emitStepSelection(
          context,
          stepId,
          step.leadFieldAssociated as string | undefined,
          step.customField,
          data,
        );
      }
    }

    for (const child of getNestedQuestions(step)) {
      const childId = child.id;
      if (!childId) continue;
      const childData = flowState[childId];
      if (!childData) continue;
      emitStepSelection(
        context,
        childId,
        child.leadFieldAssociated as string | undefined,
        child.customField,
        childData,
      );
    }
  }

  const stepFlowType = readStepFlowType(activeStep);
  if (stepFlowType) {
    setContextKey(context, 'flowType', stepFlowType);
  }

  const stepTrafficSource = readStepTrafficSource(activeStep);
  if (stepTrafficSource) {
    setContextKey(context, 'trafficSource', stepTrafficSource);
  }

  emitSubjectLabelWithFallback(context, options?.subjectLabelFallback);

  return context;
}

/**
 * Emit `context.subject_label_with_fallback` for adaptive subject copy.
 *
 * - Specific subject slug → honest `subject_label`
 * - Missing or `general` → trimmed authored fallback, if any
 * - Otherwise omit the key (templates interpolate to empty)
 */
function emitSubjectLabelWithFallback(
  context: Record<string, unknown>,
  fallback: string | undefined,
): void {
  const subject = context.subject;
  const isSpecificSubject =
    typeof subject === 'string' &&
    subject.length > 0 &&
    subject !== DCS_GENERAL_SUBJECT;

  if (isSpecificSubject) {
    const label = context.subject_label;
    if (typeof label === 'string' && label.length > 0) {
      setContextKey(context, SUBJECT_LABEL_WITH_FALLBACK_KEY, label);
    }
    return;
  }

  const trimmed = typeof fallback === 'string' ? fallback.trim() : '';
  if (trimmed.length > 0) {
    setContextKey(context, SUBJECT_LABEL_WITH_FALLBACK_KEY, trimmed);
  }
}

function getNestedQuestions(step: Step): readonly Question[] {
  if (step.type !== StepType.MULTI_QUESTION && step.type !== StepType.DCS) {
    return [];
  }
  return (step as Step & { stepQuestions?: Question[] }).stepQuestions ?? [];
}

/**
 * Expand option value(s) for step-id / customField context keys.
 * When `isArray` is true, CSV strings become flat `string[]` entries.
 * Lead fields must use {@link rawOptionValues} instead.
 */
function expandOptionValues(opt: Option | Option[]): unknown {
  if (Array.isArray(opt)) {
    const values: Array<string | boolean | null | undefined> = [];
    for (const o of opt) {
      if (o.isArray === true) {
        values.push(...splitCsvOptionValue(o.value));
      } else {
        values.push(o.value);
      }
    }
    return values;
  }

  if (opt.isArray === true) {
    return splitCsvOptionValue(opt.value);
  }

  return opt.value;
}

function rawOptionValues(opt: Option | Option[]): unknown {
  // Unexpanded option.value(s): no isArray CSV split. Multi-select stays
  // an array of raw values; a single option stays that option's value.
  return Array.isArray(opt) ? opt.map((o) => o.value) : opt.value;
}

function expandSingleOptionValue(option: Option): unknown {
  if (option.isArray === true) {
    return splitCsvOptionValue(option.value);
  }
  return option.value;
}

function splitCsvOptionValue(value: Option['value']): string[] {
  return String(value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function emitStepSelection(
  context: Record<string, unknown>,
  stepId: string,
  stepLeadField: string | undefined,
  stepCustomField: string | undefined,
  data: FlowStateEntry,
): void {
  const opt = data.option as Option | Option[] | undefined;
  if (!opt) return;

  const expandedValue = expandOptionValues(opt);
  const rawValue = rawOptionValues(opt);
  // Display labels are always a string. Multi-select joins with ", " so
  // title/heading templates stay human-readable; slug values remain
  // arrays for machine-consumed data-source templates.
  const labelValue = Array.isArray(opt)
    ? opt.map((o) => o.text).join(', ')
    : opt.text;

  setContextKey(context, stepId, expandedValue);
  setContextKey(context, `${stepId}_label`, labelValue);

  const leadField =
    (data.leadFieldAssociated as string | undefined) ?? stepLeadField;
  if (leadField) {
    // Lead stays raw (no isArray CSV expansion). May still be an array when
    // the step selection is multi-select (`Option[]`); single options stay
    // the unexpanded option.value (typically a scalar CSV string).
    setContextKey(context, leadField, rawValue);
    setContextKey(context, `${leadField}_label`, labelValue);
  }

  if (stepCustomField && stepCustomField !== leadField) {
    setContextKey(context, stepCustomField, expandedValue);
    setContextKey(context, `${stepCustomField}_label`, labelValue);
  }

  // Configurable steps with one or more `formKey` controls write Options
  // where each entry can carry its own `_meta.leadFieldAssociated` and
  // `_meta.customField`. Emit per-option aliases so downstream templates
  // can read `{{context.<handle>}}` without inspecting the step payload.
  //
  // Step-/in-flight `leadField` and step `customField` (above) win on
  // a key collision: those broader associations represent the step's
  // primary write contract and must not be silently overwritten by an
  // option-level alias of the same name.
  const options = Array.isArray(opt) ? opt : [opt];
  for (const o of options) {
    const optLeadField = o.leadFieldAssociated;
    if (optLeadField && !hasContextKey(context, optLeadField)) {
      setContextKey(context, optLeadField, o.value);
      setContextKey(context, `${optLeadField}_label`, o.text);
    }

    const optCustomField = o.customField;
    if (optCustomField && !hasContextKey(context, optCustomField)) {
      setContextKey(context, optCustomField, expandSingleOptionValue(o));
      setContextKey(context, `${optCustomField}_label`, o.text);
    }
  }
}
